import { addDays, format, isValid, parseISO, startOfYear, subDays, subMonths } from "date-fns";

type DateSpan = [string, string];

export const reportDateRanges = [
  "last7Days",
  "last30Days",
  "last90Days",
  "last12Months",
  "thisYear",
  "custom",
] as const;
export type ReportDateRange = (typeof reportDateRanges)[number];

export type ReportPeriod = {
  dateRange: ReportDateRange;
  customRange: { start: string; end: string } | null;
};

const defaultPeriod: ReportPeriod = { dateRange: "last30Days", customRange: null };

const isIsoDate = (value: string | null): value is string => value !== null && isValid(parseISO(value));

export const periodFromSearchParams = (params: URLSearchParams): ReportPeriod => {
  const range = reportDateRanges.find((r) => r === params.get("range"));
  if (!range) return defaultPeriod;
  if (range !== "custom") return { dateRange: range, customRange: null };

  const start = params.get("from");
  const end = params.get("to");
  if (!isIsoDate(start) || !isIsoDate(end)) return defaultPeriod;
  return { dateRange: "custom", customRange: { start, end } };
};

export const periodToSearchParams = (period: ReportPeriod): Record<string, string> =>
  period.dateRange === "custom" && period.customRange
    ? { range: "custom", from: period.customRange.start, to: period.customRange.end }
    : { range: period.dateRange };

type PresetDateRange = Exclude<ReportDateRange, "custom">;

export const dateRangeLabels: Record<ReportDateRange, string> = {
  last7Days: "Ostatnie 7 dni",
  last30Days: "Ostatnie 30 dni",
  last90Days: "Ostatnie 90 dni",
  last12Months: "Ostatnie 12 miesięcy",
  thisYear: "Bieżący rok",
  custom: "Własny zakres",
};

export const toIsoDate = (d: Date) => format(d, "yyyy-MM-dd");

const startOf = (range: PresetDateRange, today: Date): Date => {
  switch (range) {
    case "last7Days":
      return subDays(today, 6);
    case "last30Days":
      return subDays(today, 29);
    case "last90Days":
      return subDays(today, 89);
    case "last12Months":
      return addDays(subMonths(today, 12), 1);
    case "thisYear":
      return startOfYear(today);
  }
};

export const currentSpan = (period: ReportPeriod, today = new Date()): DateSpan => {
  if (period.dateRange === "custom") {
    if (period.customRange) return [period.customRange.start, period.customRange.end];
    return currentSpan({ dateRange: "last30Days", customRange: null }, today);
  }
  return [toIsoDate(startOf(period.dateRange, today)), toIsoDate(today)];
};
